import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { ErrorAlert } from '../components/ErrorAlert.tsx';
import { PageHeader } from '../components/PageHeader.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import { StatusBadge } from '../components/StatusBadge.tsx';
import { Button, NavCard } from '../components/ui/index.ts';
import { useAsync } from '../hooks/useAsync.ts';
import { useAuth } from '../hooks/useAuth.tsx';
import { api } from '../lib/api.ts';
import type { Tenant } from '../lib/types.ts';

/**
 * TenantListPage — TASK-07
 *
 * Displays all tenants in a razor-sharp data table:
 * - Row hover: primary left-border highlight
 * - Status badges: colored dot indicator
 * - Click row: navigate to detail
 */
export function TenantListPage() {
  const navigate = useNavigate();
  const { data, status, error, refetch } = useAsync(() =>
    api.tenants.list().then((r) => r.tenants),
  );

  const { user } = useAuth();
  const tenants = data ?? [];
  /**
   * Dar de alta un tenant es una acción NUESTRA, no del dueño de un negocio.
   *
   * El 2026-09-22 el PO entró con la cuenta del dueño y le apareció
   * «+ Nuevo tenant»: acá había un `isSuperAdmin = !!user` —cualquiera
   * autenticado— con un FIXME que decía «el backend valida la acción». Y la
   * valida: responde 403 con otro id y 409 con el propio. Así que no era una
   * fuga de permisos, era un botón que **no podía funcionar nunca**.
   *
   * **No se gatea por rol porque no se puede:** `assignedRoles` sale vacío del
   * JWT para todos —lo llenaría el Lambda pre-token de FOLLOWUP-18, que no
   * existe—, así que un dueño y un operador se ven idénticos por ahí.
   *
   * `tenantId` sí distingue: lo escribe el alta pública al crear la cuenta
   * (`registro.ts:96`). Un dueño tiene su negocio; un operador nuestro no tiene
   * uno propio, y ahí «ninguno» es la respuesta correcta. Es lo único fiable
   * del token, y alcanza para esta decisión.
   */
  const esOperadorNuestro = !!user && !user.tenantId;

  return (
    <AppShell>
      <div className="flex flex-col h-full animate-fade-in">
        <PageHeader
          title="Tenants"
          subtitle={
            status === 'success'
              ? `${tenants.length} tenant${tenants.length !== 1 ? 's' : ''} registrados`
              : undefined
          }
          actions={
            <>
              <Button variant="outline" size="sm" onClick={refetch}>
                ↺ Refrescar
              </Button>
              {esOperadorNuestro && (
                <Button
                  variant="primary"
                  size="sm"
                  id="btn-nuevo-tenant"
                  onClick={() => navigate('/tenants/new')}
                >
                  + Nuevo tenant
                </Button>
              )}
            </>
          }
        />

        <div className="flex-1 overflow-auto bg-surface-base rounded-lg border border-edge-subtle shadow-sm m-6">
          {status === 'loading' && (
            <div className="p-6">
              <Skeleton rows={6} />
            </div>
          )}

          {status === 'error' && error && (
            <div className="p-6">
              <ErrorAlert message={error} />
            </div>
          )}

          {status === 'success' && tenants.length === 0 && (
            <EmptyState
              message="No hay tenants registrados aún."
              // La misma acción, y acá estaba sin ningún gate. Para un dueño
              // la lista nunca viene vacía —su scope trae su tenant—, así que
              // no lo alcanzaba; queda igual porque es la misma regla y no
              // depende de que ese camino siga siendo inalcanzable.
              action={
                esOperadorNuestro ? (
                  <Button variant="primary" onClick={() => navigate('/tenants/new')}>
                    + Crear primer tenant
                  </Button>
                ) : undefined
              }
            />
          )}

          {status === 'success' && tenants.length > 0 && (
            <div
              data-testid="tenant-grid"
              className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 xl:grid-cols-3"
            >
              {tenants.map((tenant: Tenant) => (
                <NavCard
                  key={tenant.tenantId}
                  to={`/tenants/${tenant.tenantId}`}
                  title={tenant.name}
                  subtitle={tenant.tenantId}
                  aside={<StatusBadge status={tenant.status} />}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono">
                      {tenant.channels?.map((ch) => ch.type).join(', ') ?? 'sin canales'}
                    </span>
                    <span className="font-mono">
                      {new Date(tenant.createdAt).toLocaleDateString('es-CO')}
                    </span>
                  </div>
                </NavCard>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
